<template>
  <NotFoundPage v-if="isNotFound" :reason="isNotFound" />
  <div v-else>
    <suspense>
      <template v-if="component && entry">
        <slot :entry="entry" :isPreview="isPreview" :slug="slug"></slot>
        <component :is="component" v-bind="entry" :slug="slug" :class="clazz" />
        <slot name="after" :isPreview="isPreview" :slug="slug"></slot>
      </template>
      <template v-else-if="isLoaded">
        <slot name="fallback" :isPreview="isPreview" :slug="slug"></slot>
      </template>
      <div class="loader-container" v-else-if="showLoader">
        <div class="loader"></div>
      </div>
    </suspense>
    <template v-for="(component, index) in loadedDependencyModules" :key="index">
      <component v-if="component" :is="component" />
    </template>
  </div>
</template>

<script>
import { getEntriesByModelSlug, getComponentDependencyModules, getEntriesByKey } from '@/services/ContentfulService.js';
import { reactive, toRefs, computed, ref, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import { setContentfulState, getLoadedDependencies } from '@/compositions/useContentful.js';
import Tracking from '@/components/commons/Tracking.js';
import useLifecycleHook from '@/compositions/useLifecycleHook';

export default {
  name: 'ContentfulRenderer',
  props: {
    component: {
      type: Object,
      required: true,
    },
    contentType: {
      type: String,
      required: true,
    },
    slug: {
      type: String,
      default: null,
    },
    activeOf: {
      type: String,
      default: null,
    },
    isRequired: {
      type: Boolean,
      default: true,
    },
    isCritical: {
      type: Boolean,
      default: false,
    },
    showLoader: {
      type: Boolean,
      default: true,
    },
    class: {
      type: String,
      default: '',
    },
    onEntryNotFound: {
      type: Function,
      default: () => {},
    },
  },
  components: {
    NotFoundPage: defineAsyncComponent(() => import('@/pages/Errors/NotFoundPage.vue')),
  },

  async setup(props) {
    const isNotFound = ref('');
    const data = reactive({ entry: null });
    const isLoaded = ref(false);
    const router = useRouter();
    const isPreview = router.currentRoute.value.query?.hasOwnProperty('ctpreview');
    const { mounted } = useLifecycleHook();

    const loadedDependencies = getLoadedDependencies();

    const load = async () => {
      try {
        let slug = props.slug;
        const errorPolicy = props.isRequired ? 'none' : 'ignore';
        if (!slug) {
          if (!props.activeOf) {
            throw new Error(`ContentfulRenderer requires one of: activeOf (${props.activeOf}), slug(${slug}) for contentType: ${props.contentType}`);
          } else {
            const activeVersion = await getEntriesByModelSlug('activeVersion', props.activeOf, 2, isPreview, errorPolicy);
            const contentPageId = activeVersion?.fields?.contentPage?.sys?.id;
            if (contentPageId) {
              data.entry = await getEntriesByKey(contentPageId, false, 2, isPreview);
            } else {
              if (props.isCritical) {
                throw new Error(
                  `invalid contentPageId: ${contentPageId} passed to ContentfulRenderer for ${props.activeOf} (${activeVersion?.sys?.id})`,
                );
              }
              if (props.isRequired) {
                isNotFound.value = `invalid contentPageId: ${contentPageId} passed to ContentfulRenderer for ${props.activeOf}`;
              }
            }
          }
        } else {
          data.entry = await getEntriesByModelSlug(props.contentType, slug, 2, isPreview, errorPolicy);
        }

        if (!data.entry && typeof props.onEntryNotFound === "function") {
          props.onEntryNotFound(); 
        }

        if (data.entry) {
          setContentfulState({ currentEntry: data.entry, isPreview: isPreview });
        } else {
          if (props.isCritical) {
            //SEND ALERT
            throw new Error(
              `ContentfulRenderer failed for activeOf: ${props.activeOf}, slug: ${slug}, contentType: ${props.contentType}, isRequired:${props.isRequired} , isCritical: ${props.isCritical}`,
            );
          }
          if (props.isRequired) {
            isNotFound.value = `Content not found: ${props.contentType}/${slug} (${props.activeOf})`;
          }
        }
      } catch (error) {
        console.error(error);
        Tracking.sentryCaptureError(error);
      } finally {
        isLoaded.value = true;
      }
    };

    await load();

    const loadedDependencyModules = computed(() => {
      if (mounted.value) return getComponentDependencyModules(loadedDependencies.moduleNames);
      return null;
    });

    return {
      ...toRefs(data), //
      loadedDependencyModules,
      isPreview,
      clazz: props.class,
      isLoaded,
      isNotFound,
    };
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/mixins';
@import '@/pixel/style/variables';

.loader-container {
  width: 100%;
  height: 100%;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;

  .loader {
    @mixin loader 32px;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>
